import { createContext, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { status, Step, valueof } from 'react-joyride';
import { callFunction } from '../lib/Helpers';
import { TeamsUserCredential } from '@microsoft/teamsfx';
import { toast } from 'react-toastify';

interface TutorialsContextType {
  steps: Step[];
  running: boolean;
  runTutorial: (scope: string, callbackRun?: () => void) => void;
  setRunning: (running: boolean) => void;
  getScopeTutorials: (appId: string) => TutorialObject[];
  status: string | undefined;
  setStatus: (value: string) => void;
  switchFirstRun: (
    appId: string,
    teamsUserCredential: TeamsUserCredential
  ) => void;
}

export interface TutorialObject {
  title: string;
  code: string;
}

interface TutorialsContextProviderProps {
  children: ReactNode;
}

export const TutorialsContext = createContext({} as TutorialsContextType);

export function TutorialsContextProvider({
  children,
}: TutorialsContextProviderProps) {
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [status, setStatus] = useState<string>();

  const { t } = useTranslation('helpPanel');

  function runTutorial(scope: string, callbackRun?: () => void) {
    import(`../json/tutorials/${scope}.json`).then((file) => {
      setSteps(file.steps);
    });

    setRunning(true);
    callbackRun && callbackRun();
  }

  function switchFirstRun(
    appId: string,
    teamsUserCredential: TeamsUserCredential
  ) {
    callFunction(
      true,
      'patchContactTutorial?appId=' + appId,
      undefined,
      false,
      teamsUserCredential
    ).catch(() => {
      toast.error('Failed to disable tutorial, try again later.');
    })
  }

  function getScopeTutorials(appId: string) {
    switch (appId) {
      case '798cf522-7099-4391-b739-60e013002413':
        return [{ title: t('TTS_GUIDED_TOUR'), code: 'tts' }];
      case 'myhub':
        return [{ title: t('MY_HUB_GUIDED_TOUR'), code: 'myhub' }];
      case 'subscriptions-settings':
        return [
          {
            title: t('SUBSCRIPTIONS_GUIDED_TOUR'),
            code: 'subscriptions-settings',
          },
        ];
      case 'users-settings':
        return [
          {
            title: t('USERS_GUIDED_TOUR'),
            code: 'users-settings',
          },
        ];
      default:
        return [];
    }
  }

  return (
    <TutorialsContext.Provider
      value={{
        running,
        steps,
        runTutorial,
        setRunning,
        getScopeTutorials,
        status,
        setStatus,
        switchFirstRun,
      }}
    >
      {children}
    </TutorialsContext.Provider>
  );
}
